import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { createInvite, checkDuplicateInvite, checkExistingMember } from "@/lib/api/invites";
import { toast } from "sonner";
import {
  Check,
  ChevronDown,
  ChevronUp,
  ArrowRight,
  Cpu,
  Sparkles,
  AlertCircle,
  AlertTriangle,
  ShieldCheck,
  Zap,
  Users,
  X,
  Pencil,
  Send,
  Loader2,
  CheckCircle2,
  UserPlus,
  Mail,
} from "lucide-react";
import { DECISION_TRACKS, DECISION_SUBTRACKS, getTrackById } from "@/lib/decisionTracks";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";

interface Classification {
  decision_type: string;
  decision_subtype: string;
  confidence: number;
  reasoning: string;
  suggested_title: string;
  classification_signals?: string[];
  preliminary_impact_level?: string;
  affected_roles?: string[];
}

export interface ClassificationConfirmMeta {
  affectedRoles: string[];
  impactLevel: string;
  signals: string[];
  title: string;
}

interface StepClassificationConfirmProps {
  classification: Classification;
  userDescription: string;
  workspaceId: string;
  onConfirm: (decisionType: string, decisionSubtype: string, meta: ClassificationConfirmMeta) => void;
  onBack: () => void;
}

const CONFIDENCE_THRESHOLD = 70;
const CONFIDENCE_CRITICAL = 40;

const IMPACT_LEVELS: Record<string, { label: string; color: string; description: string }> = {
  operativ: { label: "Operativ", color: "text-muted-foreground", description: "Tagesgeschäft" },
  taktisch: { label: "Taktisch", color: "text-primary", description: "Quartalsziel" },
  strategisch: { label: "Strategisch", color: "text-amber-500", description: "Jahreshorizont" },
  existenzrelevant: { label: "Existenzrelevant", color: "text-destructive", description: "Unternehmensfortbestand" },
};

const AVAILABLE_ROLES = [
  { id: "ceo", label: "CEO" },
  { id: "cto", label: "CTO" },
  { id: "cfo", label: "CFO" },
  { id: "coo", label: "COO" },
  { id: "sales", label: "Sales" },
  { id: "product", label: "Product" },
  { id: "marketing", label: "Marketing" },
  { id: "finance", label: "Finance" },
  { id: "operations", label: "Operations" },
  { id: "hr", label: "HR" },
  { id: "engineering", label: "Engineering" },
  { id: "legal", label: "Legal" },
];

export const StepClassificationConfirm = ({
  classification,
  userDescription,
  workspaceId,
  onConfirm,
  onBack,
}: StepClassificationConfirmProps) => {
  const [selectedType, setSelectedType] = useState(classification.decision_type);
  const [selectedSubtype, setSelectedSubtype] = useState(classification.decision_subtype);
  const [showAllTracks, setShowAllTracks] = useState(classification.confidence < CONFIDENCE_THRESHOLD);
  const [isManualOverride, setIsManualOverride] = useState(false);
  const [lowConfidenceAcknowledged, setLowConfidenceAcknowledged] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState<string[]>(classification.affected_roles || []);
  const [escalationDismissed, setEscalationDismissed] = useState(false);
  const [editedTitle, setEditedTitle] = useState(classification.suggested_title || '');
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [invitedEmails, setInvitedEmails] = useState<string[]>([]);

  const isCriticalConfidence = classification.confidence < CONFIDENCE_CRITICAL;
  const isLowConfidence = classification.confidence < CONFIDENCE_THRESHOLD;
  const currentTrack = getTrackById(selectedType);
  const subtracks = DECISION_SUBTRACKS[selectedType] || [];
  const confidenceColor = classification.confidence >= 70 ? "text-emerald-600" : classification.confidence >= 40 ? "text-amber-500" : "text-destructive";

  const impactLevel = classification.preliminary_impact_level || 'taktisch';
  const impactConfig = IMPACT_LEVELS[impactLevel] || IMPACT_LEVELS.taktisch;
  const isHighImpact = impactLevel === 'strategisch' || impactLevel === 'existenzrelevant';
  const signals = classification.classification_signals || [];

  const canProceed = selectedType && selectedSubtype && (
    (!isLowConfidence) ||
    (isLowConfidence && !isCriticalConfidence && (lowConfidenceAcknowledged || isManualOverride)) ||
    (isCriticalConfidence && isManualOverride)
  );

  const handleConfirm = () => {
    onConfirm(selectedType, selectedSubtype, {
      affectedRoles: selectedRoles,
      impactLevel,
      signals,
      title: editedTitle.trim() || classification.suggested_title || '',
    });
  };

  const handleTrackChange = (trackId: string) => {
    setSelectedType(trackId);
    setIsManualOverride(trackId !== classification.decision_type);
    const subs = DECISION_SUBTRACKS[trackId] || [];
    setSelectedSubtype(subs[0]?.id || "");
  };

  const toggleRole = (roleId: string) => {
    setSelectedRoles(prev =>
      prev.includes(roleId)
        ? prev.filter(r => r !== roleId)
        : [...prev, roleId]
    );
  };

  const handleInvite = async () => {
    if (!inviteEmail || !inviteEmail.includes('@')) {
      toast.error("Bitte gib eine gültige E-Mail-Adresse ein.");
      return;
    }
    setInviteLoading(true);
    try {
      const normalizedEmail = inviteEmail.toLowerCase().trim();
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { toast.error("Nicht angemeldet."); return; }

      const isExisting = await checkExistingMember(workspaceId, normalizedEmail);
      if (isExisting) { toast.error("Bereits Mitglied."); setInviteLoading(false); return; }

      const isDuplicate = await checkDuplicateInvite(workspaceId, normalizedEmail);
      if (isDuplicate) { toast.error("Einladung existiert bereits."); setInviteLoading(false); return; }

      const invite = await createInvite({ workspaceId, email: normalizedEmail, role: 'member', createdBy: user.id });
      await supabase.functions.invoke('send-team-invite', { body: { inviteId: invite.id } });

      setInvitedEmails(prev => [...prev, normalizedEmail]);
      setInviteEmail('');
      toast.success(`Einladung an ${normalizedEmail} gesendet.`);
    } catch (err: any) {
      toast.error(err?.message || "Fehler beim Einladen.");
    } finally {
      setInviteLoading(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="space-y-3">
        <h1 className="text-2xl md:text-[36px] leading-[1.1] tracking-tight font-bold font-serif text-foreground">
          Einordnung bestätigen
        </h1>
        <p className="text-base text-muted-foreground">
          {isCriticalConfidence
            ? "Die System-Konfidenz ist sehr niedrig. Du musst den Bereich manuell auswählen."
            : isLowConfidence
              ? "Das System ist sich unsicher. Bitte prüfe die Einordnung sorgfältig."
              : "Das System hat deine Entscheidung analysiert. Bestätige oder korrigiere die Einordnung."}
        </p>
      </div>

      {/* User input summary */}
      <div className="rounded-xl border border-border/50 bg-muted/30 p-4 space-y-3">
        <p className="text-sm text-muted-foreground mb-1">Deine Beschreibung:</p>
        <p className="text-sm text-foreground leading-relaxed line-clamp-3">
          „{userDescription}"
        </p>
      </div>

      {/* Editable suggested title */}
      <div className="space-y-1.5">
        <label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
          <Pencil className="w-3 h-3" />
          Titel der Entscheidung
        </label>
        <Input
          value={editedTitle}
          onChange={(e) => setEditedTitle(e.target.value.slice(0, 80))}
          placeholder="z.B. Automotive-Verticalisierung vs. Horizontal-Fokus"
          className="bg-background/60 border-border/40 text-sm h-10"
          maxLength={80}
        />
        <p className="text-[10px] text-muted-foreground text-right">{editedTitle.length}/80</p>
      </div>

      {/* Low / Critical confidence warning banner */}
      {isLowConfidence && (
        <div className={cn(
          "rounded-xl border p-4 flex items-start gap-3 animate-fade-in",
          isCriticalConfidence
            ? "border-destructive/40 bg-destructive/[0.06]"
            : "border-amber-500/40 bg-amber-500/[0.06]"
        )}>
          <AlertTriangle className={cn("w-5 h-5 shrink-0 mt-0.5", isCriticalConfidence ? "text-destructive" : "text-amber-500")} />
          <div className="space-y-1">
            <p className="text-sm font-semibold text-foreground">
              {isCriticalConfidence
                ? `Sehr niedrige Konfidenz (${classification.confidence}%)`
                : `Niedrige Konfidenz (${classification.confidence}%)`}
            </p>
            <p className="text-xs text-muted-foreground leading-relaxed">
              {isCriticalConfidence
                ? `Die automatische Einordnung liegt unter ${CONFIDENCE_CRITICAL}%. Du musst den Bereich manuell aus der Liste unten auswählen, um fortzufahren.`
                : `Die automatische Einordnung liegt unter ${CONFIDENCE_THRESHOLD}%. Bitte überprüfe, ob der vorgeschlagene Bereich zu deiner Entscheidung passt, oder wähle manuell den richtigen Bereich aus.`}
            </p>
          </div>
        </div>
      )}

      {/* AI Classification result */}
      <div className={cn(
        "rounded-xl border p-5 space-y-4",
        isLowConfidence
          ? "border-amber-500/30 bg-amber-500/[0.02]"
          : "border-primary/30 bg-primary/[0.03]"
      )}>
        <div className="flex items-start gap-3">
          <div className={cn(
            "w-10 h-10 rounded-xl flex items-center justify-center shrink-0",
            isLowConfidence ? "bg-amber-500/10" : "bg-primary/10"
          )}>
            <Cpu className={cn("w-5 h-5", isLowConfidence ? "text-amber-500" : "text-primary")} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm font-semibold text-foreground">
                System-Einordnung
              </p>
              <span className={cn("text-xs font-medium", confidenceColor)}>
                {classification.confidence}% Konfidenz
              </span>
              {/* Impact Level Badge */}
              <span className={cn(
                "text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border",
                impactLevel === 'existenzrelevant'
                  ? "text-destructive border-destructive/30 bg-destructive/10"
                  : impactLevel === 'strategisch'
                    ? "text-amber-500 border-amber-500/30 bg-amber-500/10"
                    : impactLevel === 'taktisch'
                      ? "text-primary border-primary/30 bg-primary/10"
                      : "text-muted-foreground border-border/40 bg-muted/20"
              )}>
                {impactConfig.label}
              </span>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              {classification.reasoning}
            </p>
          </div>
        </div>

        {/* Classification Signals */}
        {signals.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground">Erkannte Signale:</p>
            <div className="flex flex-wrap gap-1.5">
              {signals.map((signal, i) => (
                <Badge
                  key={i}
                  variant="secondary"
                  className="text-xs font-normal bg-muted/50 border-border/40"
                >
                  {signal}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Selected track display */}
        <div className="flex items-center gap-3 p-3 rounded-lg bg-background/80 border border-border/50">
          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-foreground">
              {currentTrack?.label || selectedType}
            </p>
            <p className="text-xs text-muted-foreground">
              {currentTrack?.description}
            </p>
          </div>
          {!isManualOverride && !isLowConfidence && (
            <Check className="w-5 h-5 text-primary shrink-0" />
          )}
        </div>

        {/* Subtype chips */}
        {subtracks.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground">Fokus:</p>
            <div className="flex flex-wrap gap-2">
              {subtracks.map((sub) => (
                <button
                  key={sub.id}
                  onClick={() => {
                    setSelectedSubtype(sub.id);
                    if (selectedType !== classification.decision_type || sub.id !== classification.decision_subtype) {
                      setIsManualOverride(true);
                    }
                  }}
                  className={cn(
                    "px-3 py-1.5 rounded-full text-xs font-medium border transition-all",
                    selectedSubtype === sub.id
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-background border-border/60 text-muted-foreground hover:border-primary/40 hover:text-foreground"
                  )}
                >
                  {sub.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Stakeholder Scope — editable role chips */}
      <div className="rounded-xl border border-border/50 bg-card/30 p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-muted-foreground" />
          <p className="text-sm font-medium text-foreground">Betroffene Rollen</p>
          <span className="text-xs text-muted-foreground">(optional)</span>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {AVAILABLE_ROLES.map((role) => {
            const isSelected = selectedRoles.includes(role.id);
            return (
              <button
                key={role.id}
                onClick={() => toggleRole(role.id)}
                className={cn(
                  "px-2.5 py-1 rounded-full text-xs font-medium border transition-all",
                  isSelected
                    ? "bg-primary/10 text-primary border-primary/30"
                    : "bg-background border-border/40 text-muted-foreground hover:border-primary/30 hover:text-foreground"
                )}
              >
                {role.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Escalation: Actionable team invite for high-impact decisions */}
      {isHighImpact && !escalationDismissed && (
        <div className="rounded-xl border border-primary/30 bg-primary/[0.03] p-4 space-y-3 animate-fade-in">
          <div className="flex items-start gap-3">
            <Zap className="w-5 h-5 text-primary shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0 space-y-1">
              <p className="text-sm font-semibold text-foreground">
                Hohe strategische Tragweite erkannt
              </p>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Für {impactLevel === 'existenzrelevant' ? 'existenzrelevante' : 'strategische'} Entscheidungen empfehlen wir, mindestens 2 Perspektiven zu integrieren.
              </p>
            </div>
            <button
              onClick={() => setEscalationDismissed(true)}
              className="shrink-0 text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Invited emails list */}
          {invitedEmails.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {invitedEmails.map((em) => (
                <span key={em} className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20 flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" />
                  {em}
                </span>
              ))}
            </div>
          )}

          {/* Inline invite form */}
          {!showInviteForm ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowInviteForm(true)}
              className="gap-1.5 text-xs h-8 border-primary/30 text-primary hover:bg-primary/5"
            >
              <UserPlus className="w-3.5 h-3.5" />
              Teammitglied jetzt einladen
            </Button>
          ) : (
            <div className="flex gap-2 items-center animate-fade-in">
              <div className="relative flex-1">
                <Mail className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <Input
                  type="email"
                  placeholder="kollege@firma.de"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleInvite()}
                  className="pl-8 h-8 text-xs bg-background/60 border-border/40"
                  disabled={inviteLoading}
                />
              </div>
              <Button
                size="sm"
                onClick={handleInvite}
                disabled={inviteLoading || !inviteEmail}
                className="h-8 px-3 text-xs gap-1.5"
              >
                {inviteLoading ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Send className="w-3.5 h-3.5" />
                )}
                Einladen
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Low confidence acknowledgment checkbox (only for 40-70%, NOT for <40%) */}
      {isLowConfidence && !isCriticalConfidence && !isManualOverride && (
        <div className="flex items-start gap-3 rounded-lg border border-border/50 bg-muted/30 p-4 animate-fade-in">
          <Checkbox
            id="acknowledge-low-confidence"
            checked={lowConfidenceAcknowledged}
            onCheckedChange={(checked) => setLowConfidenceAcknowledged(checked === true)}
            className="mt-0.5"
          />
          <label htmlFor="acknowledge-low-confidence" className="text-sm text-foreground leading-relaxed cursor-pointer">
            <span className="font-medium">Ich bestätige:</span>{" "}
            Die vorgeschlagene Einordnung „{currentTrack?.label}" passt zu meiner Entscheidung, trotz niedriger System-Konfidenz.
          </label>
        </div>
      )}

      {/* Manual override section */}
      <Collapsible open={showAllTracks} onOpenChange={setShowAllTracks}>
        <CollapsibleTrigger asChild>
          <button className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
            <AlertCircle className="w-4 h-4" />
            <span>{isLowConfidence ? "Bereich manuell wählen" : "Einordnung stimmt nicht?"}</span>
            {showAllTracks ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-3 space-y-3 animate-fade-in">
          <p className="text-xs text-muted-foreground">
            Wähle den passenden Bereich manuell aus:
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {DECISION_TRACKS.map((track) => (
              <button
                key={track.id}
                onClick={() => handleTrackChange(track.id)}
                className={cn(
                  "flex items-center gap-3 p-3 rounded-lg border text-left transition-all text-sm",
                  selectedType === track.id
                    ? "border-primary bg-primary/5"
                    : "border-border/60 bg-card/30 hover:border-primary/30"
                )}
              >
                <div className={cn(
                  "w-2 h-2 rounded-full shrink-0",
                  selectedType === track.id ? "bg-primary" : "bg-muted-foreground/30"
                )} />
                <div>
                  <p className="font-medium text-foreground">{track.label}</p>
                  <p className="text-xs text-muted-foreground">{track.description}</p>
                </div>
              </button>
            ))}
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Dynamic consequences — what changes based on track */}
      <div className="rounded-lg bg-muted/40 border border-border/50 px-4 py-3 space-y-2">
        <p className="text-xs font-medium text-foreground/80">Was ändert sich?</p>
        <p className="text-xs text-muted-foreground leading-relaxed">
          Der Bereich bestimmt die Fragen in Phase 1, den Fokus der KI-Analyse und die Struktur des Reports.
        </p>
        {currentTrack?.focusAreas && currentTrack.focusAreas.length > 0 && (
          <div className="flex flex-wrap gap-1.5 pt-1">
            {currentTrack.focusAreas.map((area, i) => (
              <span
                key={i}
                className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-primary/8 text-primary border border-primary/15"
              >
                {area}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-3">
        <Button variant="ghost" onClick={onBack} className="px-4">
          Zurück
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={!canProceed}
          className="h-12 px-6 text-base font-semibold flex-1 sm:flex-initial gap-2"
        >
          {isManualOverride
            ? "Mit Korrektur bestätigen"
            : isCriticalConfidence
              ? "Bitte Bereich manuell wählen"
              : isLowConfidence
                ? "Einordnung bestätigen"
                : "Bestätigen & weiter"}
          {canProceed ? <ArrowRight className="w-4 h-4" /> : <ShieldCheck className="w-4 h-4" />}
        </Button>
      </div>
    </div>
  );
};
